import { use, useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  createVarsResolver,
  getRadius,
  getSize,
  MantineColor,
  MantineGradient,
  MantineRadius,
  MantineSize,
  polymorphicFactory,
  PolymorphicFactory,
  StylesApiProps,
  useProps,
  useStyles,
} from '../../core';
import {
  AvatarGroup,
  AvatarGroupContext,
  type AvatarGroupProps,
  type AvatarGroupStylesNames,
  type AvatarGroupCssVariables,
  type AvatarGroupFactory,
  type AvatarGroupContextValue,
} from './AvatarGroup/AvatarGroup';
import { AvatarPlaceholderIcon } from './AvatarPlaceholderIcon';
import { getInitialsColor } from './get-initials-color/get-initials-color';
import { getInitials } from './get-initials/get-initials';
import classes from './Avatar.module.css';
export type AvatarStylesNames = 'root' | 'placeholder' | 'image';
export type AvatarVariant =
  | 'filled'
  | 'light'
  | 'gradient'
  | 'outline'
  | 'transparent'
  | 'default'
  | 'white';

export type AvatarCssVariables = {
  root: '--avatar-size' | '--avatar-radius' | '--avatar-bg' | '--avatar-color' | '--avatar-bd';
};

export interface AvatarProps extends BoxProps, StylesApiProps<AvatarFactory> {
  /** Width and height of the avatar, numbers are converted to rem @default 'md' */
  size?: MantineSize | (string & {}) | number;

  /** Key of `theme.radius` or any valid CSS value to set border-radius @default '1000px' */
  radius?: MantineRadius;

  /** Key of `theme.colors` or any valid CSS color @default 'gray' */
  color?: MantineColor | 'initials';

  /** Gradient configuration for `variant="gradient"` @default theme.defaultGradient */
  gradient?: MantineGradient;

  /** Image url, if the image cannot be loaded or `src={null}`, then placeholder is displayed instead */
  src?: string | null;

  /** Image `alt` attribute, also used as `title` attribute for placeholder */
  alt?: string;

  /** Attributes passed down to `img` element */
  imageProps?: React.ComponentProps<'img'>;

  /** Avatar placeholder, displayed when `src={null}` or when the image cannot be loaded */
  children?: React.ReactNode;

  /** If set, adjusts text color based on background color for `filled` variant */
  autoContrast?: boolean;

  /** Name of the user. When `src` is not set, used to display initials and to generate color when `color="initials"` is set. */
  name?: string;

  /** A list of colors that is used for autogenerated initials. By default, all default Mantine colors can be used except gray and dark. */
  allowedInitialsColors?: MantineColor[];
}

export type AvatarFactory = PolymorphicFactory<{
  props: AvatarProps;
  defaultRef: HTMLDivElement;
  defaultComponent: 'div';
  stylesNames: AvatarStylesNames;
  vars: AvatarCssVariables;
  variant: AvatarVariant;
  staticComponents: {
    Group: typeof AvatarGroup;
  };
}>;

const varsResolver = createVarsResolver<AvatarFactory>(
  (
    theme,
    { size, radius, variant, gradient, color, autoContrast, name, allowedInitialsColors }
  ) => {
    const _color =
      color === 'initials' && typeof name === 'string'
        ? getInitialsColor(name, allowedInitialsColors)
        : color;

    const colors = theme.variantColorResolver({
      color: _color || 'gray',
      theme,
      gradient,
      variant: variant || 'light',
      autoContrast,
    });

    return {
      root: {
        '--avatar-size': getSize(size, 'avatar-size'),
        '--avatar-radius': radius === undefined ? undefined : getRadius(radius),
        '--avatar-bg': _color || variant ? colors.background : undefined,
        '--avatar-color': _color || variant ? colors.color : undefined,
        '--avatar-bd': _color || variant ? colors.border : undefined,
      },
    };
  }
);

export const Avatar = polymorphicFactory<AvatarFactory>((_props) => {
  const props = useProps('Avatar', null, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    vars,
    src,
    alt,
    radius,
    color,
    gradient,
    imageProps,
    children,
    autoContrast,
    mod,
    name,
    allowedInitialsColors,
    attributes,
    ...others
  } = props;
  const groupCtx = use(AvatarGroupContext);
  const [error, setError] = useState(!src);

  const getStyles = useStyles<AvatarFactory>({
    name: 'Avatar',
    props,
    classes,
    className,
    style,
    classNames,
    styles,
    unstyled,
    attributes,
    vars,
    varsResolver,
  });

  useEffect(() => setError(!src), [src]);

  return (
    <Box {...getStyles('root')} mod={[{ 'within-group': groupCtx.withinGroup }, mod]} {...others}>
      {error || !src ? (
        <span {...getStyles('placeholder')} title={alt}>
          {children || (typeof name === 'string' && getInitials(name)) || <AvatarPlaceholderIcon />}
        </span>
      ) : (
        <img
          {...imageProps}
          {...getStyles('image')}
          src={src}
          alt={alt}
          onError={(event) => {
            setError(true);
            imageProps?.onError?.(event);
          }}
        />
      )}
    </Box>
  );
});

Avatar.classes = classes;
Avatar.varsResolver = varsResolver;
Avatar.displayName = '@mantine/core/Avatar';
Avatar.Group = AvatarGroup;

export namespace Avatar {
  export type Props = AvatarProps;
  export type StylesNames = AvatarStylesNames;
  export type CssVariables = AvatarCssVariables;
  export type Variant = AvatarVariant;
  export type Factory = AvatarFactory;

  export namespace Group {
    export type Props = AvatarGroupProps;
    export type StylesNames = AvatarGroupStylesNames;
    export type CssVariables = AvatarGroupCssVariables;
    export type Factory = AvatarGroupFactory;
    export type ContextValue = AvatarGroupContextValue;
  }
}
